import { GridCellProps } from '@progress/kendo-react-grid';
import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { SyntheticEvent, useCallback, useMemo } from 'react';
import GridCellBase, { useGridCellDisplayValue } from 'src/components/GridCellBase';
import NumberInput from 'src/components/NumberInput';
import usePeriodView from 'src/hooks/usePeriodView';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { isNullOrUndefined } from 'src/utils';
import { getBgColor } from 'src/utils/icons';

const ForecastAmountCell = (props: GridCellProps) => {
  const { isEditMode, hasManageBudgetPermission, isCheckbookEditable } = useViewCheckbook();
  const { isCurrentPeriodPassed } = usePeriodView();
  const { dataItem, dataIndex, field = '', onChange } = props;
  const { isDeleted, warnings } = dataItem;
  const { displayValue, value } = useGridCellDisplayValue(props);

  const handleBudgetChange = useCallback(
    (e: TextBoxChangeEvent) => {
      onChange?.({
        dataIndex: dataIndex,
        dataItem: dataItem,
        field: field,
        syntheticEvent: e.syntheticEvent,
        value: e.value,
      });
    },
    [dataIndex, dataItem, field, onChange]
  );

  const isEditable = useMemo(
    () =>
      isEditMode &&
      hasManageBudgetPermission &&
      !isDeleted &&
      !isCurrentPeriodPassed &&
      isCheckbookEditable,
    [hasManageBudgetPermission, isCurrentPeriodPassed, isDeleted, isEditMode, isCheckbookEditable]
  );

  const handleBlur = useCallback(() => {
    if (isEditable) {
      const newValue = isNullOrUndefined(value) || value === '' ? '0' : value;
      const dummyEvent = {} as SyntheticEvent;

      onChange?.({
        dataIndex: dataIndex,
        dataItem: dataItem,
        field: field,
        syntheticEvent: dummyEvent,
        value: newValue,
      });
    }
  }, [dataIndex, dataItem, field, isEditable, onChange, value]);

  return (
    <GridCellBase
      {...props}
      title={displayValue}
      className={`${getBgColor([], warnings?.budgetAmount as string[])} !text-end ${
        !isEditMode && Number(value) < 0 ? 'budget-cell-color' : ''
      }`}
      rowSelectionEnabled
    >
      {isEditable ? (
        <NumberInput
          value={isNullOrUndefined(value) ? '0' : String(value)}
          onChange={handleBudgetChange}
          prefix="$"
          onBlur={handleBlur}
        />
      ) : (
        <span>{displayValue}</span>
      )}
    </GridCellBase>
  );
};

export default ForecastAmountCell;
